import React, {ComponentType, FunctionComponent, PropsWithChildren, Suspense, useEffect, useMemo} from 'react';
import {createBrowserRouter, createRoutesFromElements, RouterProvider} from "react-router-dom";
import {HelmetProvider} from "react-helmet-async";
import {StyledEngineProvider, Theme, ThemeProvider} from "@mui/material/styles";
import {getCookieConsentValue} from "react-cookie-consent";
import mixpanel from "mixpanel-browser/src/loaders/loader-module-core";
import {logErrorToBackend} from "./utils/erroryLoggingUtils";
import {startAnalytics} from "./utils/analyticsStartup";
import {ConsentCookiesProvider, removePersonalCookie} from "./contexts/CookieSettingsContext/CookieSettingsContext";
import {ThemePreferenceProvider, ThemePreferences, useThemePreference} from "./contexts/ThemePreferenceContext/ThemePreferenceProvider";
import {getArtistreeMaterialUIStyling} from "./artistree/artistreeStyles/ArtistreeMaterialUIStyling";
import LoadingGif from "./components/LoadingGif/LoadingGif";
import {LoadingGifProps} from "./components/LoadingBase/LoadingBase";
import {toRoute} from "./utils/RouterUtils";
import {lazyWithRetry} from "./utils/LazyLoadingUtils";
import {isHumanSite} from "./utils/siteUtils";
import "./App.scss";
import "./styles/LayoutUtilities.scss";
import "./styles/StylingUtilities.scss";


if (process.env.NODE_ENV === 'production') {
  console.log = function () {
  };
}

const isHuman = isHumanSite();

const importHumanRoutes = () => import("./human/humanComponents/HumanRoutes/HumanRoutes");
const HumanRoutesComp = lazyWithRetry(importHumanRoutes);
const ArtistreeRoutesComp = lazyWithRetry(() => import("./artistree/artistreeComponents/ArtistreeRoutes/ArtistreeRoutes"));

// Persistence stays on before consent on purpose: the backend keys its request-form block list and server-side
// analytics on the Client-Id header (see fetchUtils), which is only stable while mixpanel keeps its distinct id.
// Tracking itself is gated by has_opted_in_tracking() in MixPanelUtils.
mixpanel.init(process.env['REACT_APP_MIXPANEL_TOKEN'] as string);

const router = createBrowserRouter(createRoutesFromElements(
  toRoute({route: "/*", element: isHuman ? <HumanRoutesComp/> : <ArtistreeRoutesComp/>}, 0)));

type SiteChrome = {
  getTheme: (themePreference: ThemePreferences, isDarkTheme: boolean) => Theme,
  LoadingGif: ComponentType<LoadingGifProps>,
}

const artistreeChrome: SiteChrome = {getTheme: getArtistreeMaterialUIStyling, LoadingGif};

const ThemeProviderWrapper: FunctionComponent<PropsWithChildren<{ getTheme: SiteChrome["getTheme"] }>> = ({getTheme, children}) => {
  const {themePreference, isDarkTheme} = useThemePreference();
  const isDark = isDarkTheme();
  const theme = useMemo(() => getTheme(themePreference, isDark), [getTheme, themePreference, isDark]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

const SiteRoot: FunctionComponent<{ chrome: SiteChrome }> = ({chrome: {getTheme, LoadingGif: SiteLoadingGif}}) => (
  <ThemeProviderWrapper getTheme={getTheme}>
    <ConsentCookiesProvider>
      <Suspense fallback={<SiteLoadingGif style={{height: "100vh"}}/>}>
        <RouterProvider router={router}/>
      </Suspense>
    </ConsentCookiesProvider>
  </ThemeProviderWrapper>
);

// The Human theme and loading gif only ship in the Human chunk. Its routes chunk is kicked off at the same time so the
// two download in parallel instead of one after the other.
const HumanSiteRoot = lazyWithRetry(() => {
  importHumanRoutes().catch(() => undefined);

  return Promise.all([
    import("./human/humanStyles/HumanMaterialUIStyling"),
    import("./human/humanComponents/HumanLoadingGif/HumanLoadingGif"),
  ]).then(([styling, loadingGif]) => {
    const chrome: SiteChrome = {
      getTheme: (themePreference) => styling.getHumanMaterialUIStyling(themePreference, true),
      LoadingGif: loadingGif.default,
    };
    return {default: () => <SiteRoot chrome={chrome}/>};
  });
});

const App: FunctionComponent = () => {

  useEffect(() => {
    if (getCookieConsentValue() === "true") {
      startAnalytics();
    } else {
      // this is done here because, I believe, there is a delay of when Google's GA stops working
      // so doing this right after the button is clicked isn't working
      removePersonalCookie();
    }
  }, [])

  return (
    <React.StrictMode>
      <HelmetProvider>
        <ThemePreferenceProvider>
          <Suspense fallback={null}>
            {isHuman ? <HumanSiteRoot/> : <SiteRoot chrome={artistreeChrome}/>}
          </Suspense>
        </ThemePreferenceProvider>
      </HelmetProvider>
    </React.StrictMode>
  )
}

export default App;

// human routes are using Sentry, no need to use this
if (!isHuman) {
  window.onunhandledrejection = (event: PromiseRejectionEvent) => {

    const error = event.reason;

    if (!error) {
      // Nothing to report
      return;
    }

    // @ts-expect-error the rejection target is the window, whose location TS does not know about here
    const errorPathname = event.target?.location?.pathname || window.location.pathname;
    const fullPath = window.location.origin + errorPathname;

    if (error.name === "RethrownError") {
      logErrorToBackend(error, {componentStack: error.original_error.stack}, "rethrowErr uncaught at: " + fullPath);
      return;
    }

    logErrorToBackend(error, {componentStack: event.promise}, "uncaught promise at: " + fullPath);
  };

  window.onerror = (message, source, lineno, colno, error) => {
    logErrorToBackend(error, {message, source, lineno, colno}, "uncaught error at: " + window.location.origin + window.location.pathname);
  }
}
